import { mapHttpErrorToMessage } from "../../utils/mapHttpErrorToMessage";
import { compareClassroomNames, compareClassroomTypes } from "../SchoolStructure/classrooms/classroomSorting";
import { EVENT_TYPES, SCHOOL_YEAR_STORAGE_KEY, SCOPES } from "./constants";
import type { Classroom, EntityId, OfficialSchoolHoliday, SchoolCalendarEvent, SchoolYear } from "./types";

export const normalizeList = <T,>(payload: unknown): T[] => {
  if (Array.isArray(payload)) return payload as T[];
  const record =
    payload && typeof payload === "object"
      ? (payload as Record<string, unknown>)
      : {};
  if (Array.isArray(record.content)) return record.content as T[];
  if (Array.isArray(record.items)) return record.items as T[];
  if (Array.isArray(record.events)) return record.events as T[];
  return [];
};

export const eventId = (event?: SchoolCalendarEvent | null) =>
  event?.uuid ?? event?.id ?? event?.eventId ?? null;

export const entityId = (
  entity?: Classroom | SchoolYear | SchoolCalendarEvent | null,
): string | number => {
  return (
    entity?.uuid ??
    entity?.id ??
    entity?.classroomUuid ??
    entity?.classroomId ??
    entity?.schoolYearUuid ??
    entity?.schoolYearId ??
    ""
  ) as string | number;
};

export const looksLikeUuid = (value: unknown) =>
  typeof value === "string" &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    value.trim(),
  );

export const normalizeClassroomLabel = (value: unknown) => {
  if (!value) return "-";
  return String(value)
    .replace(/\bkindergarten\b/gi, "Kleuterklas")
    .replace(/\bKINDERGARTEN\b/g, "Kleuterklas")
    .replace(/\bgrade\b/gi, "Klas")
    .replace(/\bgroep\b/gi, "Klas");
};

export const rawClassroomName = (classroom?: Classroom | null) =>
  classroom?.className ??
  classroom?.name ??
  classroom?.label ??
  classroom?.classroomName ??
  "";

export const classroomName = (classroom?: Classroom | null) => {
  const classType = String(
    classroom?.classType ?? classroom?.type ?? "",
  ).toUpperCase();
  const grade = classroom?.grade ?? classroom?.classGrade ?? classroom?.year;
  const rawName = rawClassroomName(classroom);

  if (classType === "KINDERGARTEN") {
    return `Kleuterklas${grade != null && grade !== "" ? ` ${grade}` : ""}`;
  }

  if (classType === "GRADE" && (!rawName || /\bgrade\b/i.test(rawName))) {
    return `Klas${grade != null && grade !== "" ? ` ${grade}` : ""}`;
  }

  return normalizeClassroomLabel(rawName || "Klas zonder naam");
};

export const compareClassrooms = (a: Classroom, b: Classroom): number =>
  compareClassroomTypes(
    { classType: String(a.classType ?? a.type ?? "").toUpperCase() },
    { classType: String(b.classType ?? b.type ?? "").toUpperCase() },
  ) ||
  compareClassroomNames(classroomName(a), classroomName(b)) ||
  compareClassroomNames(String(rawClassroomName(a)), String(rawClassroomName(b)));

export const schoolYearId = (
  value?: (SchoolCalendarEvent | Classroom | SchoolYear) | null,
) => {
  const nestedSchoolYear =
    value?.schoolYear && typeof value.schoolYear === "object"
      ? value.schoolYear
      : null;

  return (
    value?.schoolYearUuid ??
    value?.schoolYearId ??
    nestedSchoolYear?.uuid ??
    nestedSchoolYear?.id ??
    value?.school_year_uuid ??
    value?.school_year_id ??
    value?.id ??
    ""
  );
};

export const parseLocalIsoDate = (value) => {
  if (!value) return null;

  if (Array.isArray(value) && value.length >= 3) {
    const [year, month, day] = value;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  const raw = String(value).trim();

  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  const dmyMatch = raw.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (dmyMatch) {
    const [, day, month, year] = dmyMatch;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  return null;
};

export const getSchoolYearDateRange = (schoolYear) => {
  if (!schoolYear) return { startDate: null, endDate: null };

  const startRaw =
    schoolYear.startDate ??
    schoolYear.start_date ??
    schoolYear.schoolYearStartDate ??
    schoolYear.school_year_start_date ??
    schoolYear.validFrom ??
    schoolYear.valid_from ??
    null;

  const endRaw =
    schoolYear.endDate ??
    schoolYear.end_date ??
    schoolYear.schoolYearEndDate ??
    schoolYear.school_year_end_date ??
    schoolYear.validTo ??
    schoolYear.valid_to ??
    null;

  return {
    startDate: parseLocalIsoDate(startRaw),
    endDate: parseLocalIsoDate(endRaw),
  };
};

export const startOfLocalDay = (value = new Date()) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return new Date();
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

export const hasExplicitActiveFlag = (year) =>
  year?.current === true ||
  year?.isCurrent === true ||
  year?.active === true ||
  year?.isActive === true ||
  year?.currentSchoolYear === true ||
  String(year?.status || "").toUpperCase() === "ACTIVE";

export const isSchoolYearInDateRange = (schoolYear, today = new Date()) => {
  const { startDate, endDate } = getSchoolYearDateRange(schoolYear);
  if (startDate && endDate) {
    const day = startOfLocalDay(today);
    return startDate <= day && day <= endDate;
  }
  return false;
};

export const isSchoolYearActive = (schoolYear, today = new Date()) => {
  const { startDate, endDate } = getSchoolYearDateRange(schoolYear);
  if (startDate && endDate) {
    return isSchoolYearInDateRange(schoolYear, today);
  }
  return hasExplicitActiveFlag(schoolYear);
};

export const normalizeModalError = (err, t, fallbackMessage) => {
  const mapped = mapHttpErrorToMessage(err, t);
  const fallback =
    typeof fallbackMessage === "string" && fallbackMessage
      ? fallbackMessage
      : t("error.error_message", { defaultValue: "Something went wrong." });

  if (typeof mapped === "string") {
    return {
      title: t("common.modal.error", { defaultValue: "Fout" }),
      message: mapped,
      status: err?.response?.status,
    };
  }

  return {
    title:
      typeof mapped?.title === "string" && mapped.title
        ? mapped.title
        : t("common.modal.error", { defaultValue: "Fout" }),
    message:
      typeof mapped?.message === "string" && mapped.message
        ? mapped.message
        : fallback,
    status: mapped?.status ?? err?.response?.status,
  };
};

export const schoolYearLabel = (year) => {
  if (!year) return "Schooljaar zonder label";

  const directLabel =
    year.label ??
    year.name ??
    year.schoolYear ??
    year.schoolYearLabel ??
    year.yearLabel ??
    year.displayName ??
    year.description ??
    "";

  if (directLabel && !looksLikeUuid(String(directLabel))) {
    return String(directLabel);
  }

  const startYear =
    year.startYear ??
    year.schoolYearStartYear ??
    year.yearStart ??
    year.fromYear ??
    null;

  const endYear =
    year.endYear ??
    year.schoolYearEndYear ??
    year.yearEnd ??
    year.toYear ??
    null;

  if (startYear && endYear) return `${startYear}-${endYear}`;

  const { startDate, endDate } = getSchoolYearDateRange(year);
  if (startDate && endDate) {
    return `${startDate.getFullYear()}-${endDate.getFullYear()}`;
  }

  if (year.year) {
    const parsedYear = Number(year.year);
    if (!Number.isNaN(parsedYear)) return `${parsedYear}-${parsedYear + 1}`;
    if (!looksLikeUuid(String(year.year))) return String(year.year);
  }

  return "Schooljaar zonder label";
};

export const classroomLabel = (classroom) => {
  const year =
    classroom?.schoolYear ??
    classroom?.yearDto ??
    classroom?.schoolYearDto ??
    classroom?.academicYearObject ??
    null;

  const directYearLabel =
    classroom?.schoolYearLabel ??
    classroom?.schoolYearName ??
    classroom?.yearLabel ??
    classroom?.academicYear ??
    "";

  const label =
    directYearLabel && !looksLikeUuid(String(directYearLabel))
      ? String(directYearLabel)
      : year
        ? schoolYearLabel(year)
        : "";

  return label && label !== "Schooljaar zonder label"
    ? `${classroomName(classroom)} — ${label}`
    : classroomName(classroom);
};

export const getStoredSchoolYearUuid = () => {
  try {
    if (typeof window === "undefined") return "";
    return window.localStorage.getItem(SCHOOL_YEAR_STORAGE_KEY) || "";
  } catch {
    return "";
  }
};

export const storeSchoolYearUuid = (value) => {
  try {
    if (typeof window === "undefined") return;
    if (value) window.localStorage.setItem(SCHOOL_YEAR_STORAGE_KEY, value);
    else window.localStorage.removeItem(SCHOOL_YEAR_STORAGE_KEY);
  } catch {
    // ignore storage errors
  }
};

export const isCurrentSchoolYear = (year) => isSchoolYearInDateRange(year);

export const eventScope = (event) =>
  SCOPES.includes(event?.scope) ? event.scope : "SCHOOL";

export const eventClassroomUuids = (event) => {
  if (Array.isArray(event?.classroomUuids))
    return event.classroomUuids.map(String);
  if (Array.isArray(event?.classroomIds)) return event.classroomIds.map(String);
  if (Array.isArray(event?.classrooms)) {
    return event.classrooms.map(entityId).filter(Boolean).map(String);
  }
  return [];
};

export const eventClassroomNames = (event, classroomLookup) => {
  if (Array.isArray(event?.classroomNames) && event.classroomNames.length) {
    return event.classroomNames.filter(Boolean).map(normalizeClassroomLabel);
  }

  if (Array.isArray(event?.classrooms) && event.classrooms.length) {
    return event.classrooms
      .map(classroomName)
      .filter((name) => name && name !== "-");
  }

  return eventClassroomUuids(event)
    .map((id) => classroomLookup.get(String(id)))
    .filter(Boolean)
    .map(classroomName);
};

export const getDatePart = (value) => {
  if (!value) return "";

  if (Array.isArray(value) && value.length >= 3) {
    const [year, month, day] = value;
    return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) return trimmed.slice(0, 10);
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
};

export const getTimePart = (value) => {
  if (!value) return "";

  if (Array.isArray(value) && value.length >= 5) {
    const hour = String(value[3]).padStart(2, "0");
    const minute = String(value[4]).padStart(2, "0");
    return `${hour}:${minute}`;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();

    const isoMatch = trimmed.match(/T(\d{2}):(\d{2})/);
    if (isoMatch) return `${isoMatch[1]}:${isoMatch[2]}`;

    const timeOnlyMatch = trimmed.match(/^(\d{1,2}):(\d{2})/);
    if (timeOnlyMatch) {
      return `${String(timeOnlyMatch[1]).padStart(2, "0")}:${timeOnlyMatch[2]}`;
    }
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleTimeString("nl-NL", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

export const toTime = (value) => {
  if (!value) return null;

  if (Array.isArray(value) && value.length >= 3) {
    const [year, month, day, hour = 0, minute = 0, second = 0] = value;
    return new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second),
    ).getTime();
  }

  const time = new Date(value).getTime();
  return Number.isNaN(time) ? null : time;
};

export const buildCalendarDateTime = (date, time, allDay, isEnd) => {
  if (!date) return "";
  if (allDay) return `${date}T${isEnd ? "23:59:59" : "00:00:00"}`;
  if (!time) return `${date}T00:00:00`;
  return `${date}T${time}:00`;
};

export const compareDateTime = (date, time, allDay) => {
  if (!date) return "";
  return `${date}T${allDay ? "00:00" : time || "00:00"}`;
};

export const formatDateOnly = (value, locale = "nl-NL") => {
  const datePart = getDatePart(value);
  if (!datePart) return "-";

  const [year, month, day] = datePart.split("-");
  if (!year || !month || !day) return datePart;

  return new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
  ).toLocaleDateString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

export const normalizeOfficialHolidayTitle = (value: unknown) => {
  const raw = String(value || "").trim();
  if (!raw) return "Vakantie";
  return raw.charAt(0).toUpperCase() + raw.slice(1);
};

export const normalizeOfficialHolidayRegion = (value: unknown) => {
  const raw = String(value || "").trim();
  if (!raw) return "Landelijk";
  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
};

export const getOfficialHolidayDate = (value: unknown) => getDatePart(value);

export const flattenOfficialHolidayPayload = (payload: unknown): unknown[] => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];

  const record = payload as Record<string, unknown>;
  const candidates = [
    record.content,
    record.items,
    record.results,
    record.result,
    record.data,
    record.holidays,
    record.schoolholidays,
    record.schoolHolidays,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;
  }

  return [];
};

export const normalizeOfficialHolidays = (
  payload: unknown,
): OfficialSchoolHoliday[] => {
  const rawItems = flattenOfficialHolidayPayload(payload);

  return rawItems
    .flatMap((item) => {
      if (!item || typeof item !== "object") return [];
      const record = item as Record<string, unknown>;
      const embeddedVacations = [
        record.vacations,
        record.holidays,
        record.schoolholidays,
        record.schoolHolidays,
        record.content,
      ].find(Array.isArray) as unknown[] | undefined;

      const rows = embeddedVacations?.length
        ? embeddedVacations.map((holiday) => ({
            ...(holiday as Record<string, unknown>),
            schoolyear:
              (holiday as Record<string, unknown>)?.schoolyear ??
              record.schoolyear,
          }))
        : [record];

      return rows.map((row) => {
        const holiday = row as Record<string, unknown>;
        const title = normalizeOfficialHolidayTitle(
          holiday.title ?? holiday.name ?? holiday.type ?? holiday.holidayType,
        );
        const region = normalizeOfficialHolidayRegion(
          holiday.region ?? holiday.regio ?? holiday.location,
        );
        const startDate = getOfficialHolidayDate(
          holiday.startdate ??
            holiday.startDate ??
            holiday.start_date ??
            holiday.date_start,
        );
        const endDate =
          getOfficialHolidayDate(
            holiday.enddate ??
              holiday.endDate ??
              holiday.end_date ??
              holiday.date_end,
          ) || startDate;

        return {
          id: String(
            holiday.id ??
              `${title}-${region}-${startDate}-${endDate}`.toLowerCase(),
          ),
          title,
          region,
          startDate,
          endDate,
          notice: String(holiday.notice ?? holiday.description ?? "").trim(),
        };
      });
    })
    .filter((holiday) => holiday.startDate && holiday.endDate);
};

export const formatTime24 = (value) => {
  const time = getTimePart(value);
  return time && time !== "-" ? time : "";
};

export const formatEventTime = (event, t) => {
  const start = formatTime24(event?.startDate);
  const end = formatTime24(event?.endDate);

  if (start && end && start !== "00:00" && end !== "00:00") {
    return `${start}-${end}`;
  }

  if (event?.allDay) {
    return t("schoolCalendar.fields.allDay", { defaultValue: "Hele dag" });
  }

  if (start && end) return `${start}-${end}`;
  return start || end || "-";
};


export const buildPayloadFromEvent = (event, overrides = {}) => {
  const scope = eventScope(event);
  return {
    scope,
    classroomUuids: scope === "CLASSROOM" ? eventClassroomUuids(event) : [],
    schoolYearUuid: schoolYearId(event) || "",
    title: event?.title || "",
    description: event?.description || "",
    eventType: EVENT_TYPES.includes(event?.eventType)
      ? event.eventType
      : "OTHER",
    startDate: event?.startDate || "",
    endDate: event?.endDate || "",
    allDay: Boolean(event?.allDay),
    location: event?.location || "",
    published: Boolean(event?.published),
    ...overrides,
  };
};

/** True when every word of the query occurs in the title, description or location of the item (any case). */
export const matchesEventQuery = (
  event: { title?: string | null; description?: string | null; location?: string | null } | null | undefined,
  query: string | null | undefined,
): boolean => {
  const words = (query || "").trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const text = [event?.title, event?.description, event?.location].filter(Boolean).join(" ").toLowerCase();
  return words.every((word) => text.includes(word));
};

/** Durations offered when creating an item, in minutes. */
export const DURATION_OPTIONS = [15, 30, 45, 60, 90, 120, 150, 180, 240, 300, 360, 480];

/** "15 min", "0,5 uur", "1 uur", "1,5 uur". */
export const durationLabel = (minutes: number, hourWord: string, minuteWord: string): string => {
  if (minutes < 60 && minutes !== 30) return `${minutes} ${minuteWord}`;
  const hours = minutes / 60;
  return `${String(hours).replace(".", ",")} ${hourWord}`;
};

const pad2 = (value: number) => String(value).padStart(2, "0");

/** Date ("YYYY-MM-DD") and time ("HH:mm") plus a number of minutes, possibly into the next day. */
export const addMinutesToDateTime = (date: string, time: string, minutes: number): { date: string; time: string } | null => {
  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date || "");
  const timeMatch = /^(\d{2}):(\d{2})/.exec(time || "");
  if (!dateMatch || !timeMatch) return null;
  const moment = new Date(
    Number(dateMatch[1]), Number(dateMatch[2]) - 1, Number(dateMatch[3]),
    Number(timeMatch[1]), Number(timeMatch[2]) + minutes,
  );
  return {
    date: `${moment.getFullYear()}-${pad2(moment.getMonth() + 1)}-${pad2(moment.getDate())}`,
    time: `${pad2(moment.getHours())}:${pad2(moment.getMinutes())}`,
  };
};

/** Minutes from start to end, or null when one of them is missing. */
export const minutesBetween = (startDate: string, startTime: string, endDate: string, endTime: string): number | null => {
  const start = addMinutesToDateTime(startDate, startTime, 0);
  const end = addMinutesToDateTime(endDate, endTime, 0);
  if (!start || !end) return null;
  const toTime = (value: { date: string; time: string }) => new Date(`${value.date}T${value.time}:00`).getTime();
  return Math.round((toTime(end) - toTime(start)) / 60000);
};
